import Webcam from "react-webcam";
import * as faceapi from "face-api.js";

import { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";

import {
  Grid,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Box,
  Button,
  Typography,
  IconButton,
} from "@mui/material";

function SelfieField({ displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const webcamRef = useRef(`${fieldRef}_${displayName}`);
  const [imgSrc, setImgSrc] = useState(null);
  const [capturedImg, setCapturedImg] = useState(null);
  const [cameraUsed, setCameraUsed] = useState(false);

  const videoConstraints = {
    facingMode: cameraUsed ? "user" : { exact: "environment" },
  };

  const [openWebCamDialog, setOpenWebCamDialog] = useState(false);
  const [faceDetectionError, showFaceDetectionError] = useState(false);

  async function validateFace(bas64ImageData) {
    await faceapi.loadTinyFaceDetectorModel("/face_models");
    const base64Response = await fetch(bas64ImageData);
    const blob = await base64Response.blob();
    const image = await faceapi.bufferToImage(blob);

    const detection = await faceapi.detectSingleFace(
      image,
      new faceapi.TinyFaceDetectorOptions(),
    );

    if (!detection) {
      showFaceDetectionError(true);
      return false;
    }

    return true;
  }

  const capture = useCallback(async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      setErrorMsg("No photo image found.");
      showFieldError(true);
      return;
    }

    const passed = await validateFace(imageSrc);
    if (passed) {
      setImgSrc(imageSrc);
      setCapturedImg(imageSrc);
      setOpenWebCamDialog(false);
    }
  }, [webcamRef]);

  return (
    <Box>
      {/* ERROR */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={() => showFieldError(false)}
        open={fieldError}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* CAMERA DIALOG */}
      <Dialog open={openWebCamDialog}>
        <DialogTitle>
          <Box>{displayName}</Box>
        </DialogTitle>

        <DialogContent>
          <Box mt={2} mb={3}>
            <Webcam
              width="100%"
              ref={webcamRef}
              screenshotQuality={1}
              videoConstraints={videoConstraints}
              mirrored={cameraUsed}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenWebCamDialog(false)} variant="outlined">
            <Icon>close</Icon>
          </Button>

          <Button
            onClick={() => setCameraUsed(!cameraUsed)}
            variant="outlined"
            color="warning"
          >
            <Icon>cameraswitch</Icon>
          </Button>

          <Button onClick={capture} variant="contained" color="info">
            <Icon>photo_camera</Icon>
          </Button>
        </DialogActions>

        {/* FACE ERROR */}
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={5000}
          onClose={() => showFaceDetectionError(false)}
          open={faceDetectionError}
        >
          <Alert severity="error" sx={{ width: "100%" }}>
            Unable to detect your face.
          </Alert>
        </Snackbar>
      </Dialog>

      {/* HEADER */}
      <Box sx={{ minWidth: 120, width: "100%" }}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="h6" fontWeight="light" fontSize="small" mb={2}>
              {displayName}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Box textAlign="right">
              <IconButton
                color="primary"
                onClick={() => setOpenWebCamDialog(true)}
              >
                <Icon>camera</Icon>
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        {/* ✅ CAPTURE BOX UI */}
        <Box
          sx={{
            border: "2px dashed #d2d6da",
            borderRadius: "12px",
            height: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#f8f9fa",
            mt: 2,
            cursor: "pointer",
          }}
          onClick={() => !imgSrc && setOpenWebCamDialog(true)}
        >
          {!imgSrc && (
            <Box textAlign="center">
              <IconButton
                color="primary"
                sx={{
                  width: 64,
                  height: 64,
                  backgroundColor: "#e3f2fd",
                }}
              >
                <Icon fontSize="large">photo_camera</Icon>
              </IconButton>

              <Typography variant="caption" mt={1} display="block">
                Tap to take photo
              </Typography>
            </Box>
          )}

          {imgSrc && (
            <>
              <img
                src={imgSrc}
                alt="captured"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />

              {/* CLEAR BUTTON */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setImgSrc(null);
                  setCapturedImg(null);
                }}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  backgroundColor: "white",
                  border: "1px solid rgba(0,0,0,0.2)",
                  boxShadow: 3,
                }}
              >
                <Icon color="error">close</Icon>
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      <input
        type="hidden"
        id={`dff_${fieldRef}`}
        name={fieldRef}
        value={capturedImg}
      />
    </Box>
  );
}

SelfieField.propTypes = {
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

SelfieField.defaultProps = {
  options: null,
};

export default SelfieField;
